import { createFormatters } from '@repo/utils'
import { formatUnits } from 'viem'

/**
 * Pre-configured formatters for common numeric formats.
 *
 * Uses US locale and readable formatting by default.
 *
 * Available formatters:
 * - `hex` - Hexadecimal format
 * - `number` - Plain number format (12345.6789)
 * - `commas` - Comma-separated format (12,345.6789)
 * - `short` - Compact notation (12.3k, 1.2M)
 * - `percent` - Percentage format (multiplies by 100)
 *
 * @example
 * ```tsx
 * // Format a percentage with 2 digits and sign
 * formatters.percent.digits(2).unit("%").sign().of(0.0523)
 * // Output: "+5.23%"
 *
 * // Format a number with commas
 * formatters.commas.digits(2).of(1234567.89)
 * // Output: "1,234,567.89"
 *
 * // Format a large number in short notation
 * formatters.short.digits(2).of(1234567)
 * // Output: "1.23M"
 * ```
 */
export const formatters = createFormatters({ readable: true })

/**
 * Determines the number of digits to use for formatting based on the given price.
 *
 * When called with a single bigint arg (legacy), applies high-value thresholds
 * (>= 1000 → 4, >= 10000 → 6, >= 100000 → 8) for token amount precision.
 *
 * When called with explicit decimals or a number, only increases digits for
 * sub-1 values to show ~3 significant figures (e.g., 0.00419 → 5 digits).
 *
 * @param price - The price as a bigint (with decimal scale) or number (float).
 * @param decimals - The decimal scale of the price (only used for bigint).
 * @returns The number of digits to use for formatting.
 *
 * @example
 * getDigitsFromPrice(parseUnits("100000", 18)) // 8 (legacy high-value)
 * getDigitsFromPrice(parseUnits("0.00419", 36), 36) // 5
 * getDigitsFromPrice(0.00419) // 5
 */
export const getDigitsFromPrice = (price?: bigint | number | null, decimals?: number): number => {
  if (!price || price < 0) {
    return 2
  }

  if (typeof price === 'number') {
    if (price < 1) {
      const leadingZeros = Math.ceil(-Math.log10(price)) - 1
      return Math.min(leadingZeros + 3, 20)
    }
    return 2
  }

  const scale = decimals ?? 18
  const totalDigits = price.toString().length
  const integerDigits = totalDigits - scale

  // High-value thresholds only for legacy callers (no explicit decimals)
  if (decimals === undefined) {
    if (integerDigits >= 6) return 8 // >= 100,000
    if (integerDigits >= 5) return 6 // >= 10,000
    if (integerDigits >= 4) return 4 // >= 1,000
  }

  // Low values (< 1): show ~3 significant figures
  if (integerDigits < 1) {
    const leadingZeros = scale - totalDigits
    return Math.min(leadingZeros + 3, 20)
  }

  return 2
}

/**
 * Determines the number of digits to use for formatting based on the given price and amount.
 *
 * @param {bigint} price - The price of the token.
 * @param {bigint} amount - The amount of the token.
 * @param {number} decimals - The decimals of the token (if provided and if the format amount is greater than 1000, we use 2 digits)
 * @returns {number} The number of digits to use for formatting.
 *
 * @example
 * const digits = getDigitsFromPriceAndAmount(150000000000000000000000n, 100000000000000000000000n);
 * console.log(digits); // 8
 *
 * @example
 * const digits = getDigitsFromPriceAndAmount(150000000000000000000000n);
 * console.log(digits); // 2
 */
export const getDigitsFromPriceAndAmount = (
  price?: bigint,
  amount?: bigint | null,
  decimals?: number
): number => {
  if (!amount) {
    return 2
  }
  // If the amount is greater than 1000, we use 2 digits
  if (decimals && Number(formatUnits(amount, decimals)) > 1000) {
    return 2
  }
  return getDigitsFromPrice(price)
}
